import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { Spinner, Tooltip, Tabs, Tab } from "@heroui/react";
import { RepeatIcon, LinkSimpleIcon, XIcon, CalendarIcon, CheckIcon, UserIcon, ClockIcon, UsersThreeIcon, CaretDownIcon, CaretUpIcon, FileTextIcon, PencilSimpleIcon, TrashIcon, FloppyDiskIcon } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { applyMask } from "shared/utils/masks";
import { DashboardBlock, DashboardBlockHeader, DashboardBlockContent } from "../layout/DashboardBlock";
import { BlockLoadingState, BlockEmptyState } from "../states/BlockStates";

// Import the types from the original component
interface VacancyUnit {
  id: string;
  unitNumber: string;
  building: string;
  erv: number;
  vacantSince?: Date;
  contractEndDate?: Date;
  daysVacant?: number;
  lostRevenue?: number;
  status: 'upcoming' | 'current';
  urgencyLevel?: 'low' | 'medium' | 'high';
  lastTenantNames?: string;
  vacancyReason?: 'contract_end' | 'since_acquisition';
  unitBuiltAt?: Date;
  buildingAcquisitionDate?: Date;
  immowebReferenceId?: number;
  immowebReferenceIdAddedTimestamp?: Date | string;
  visibleInCalendar?: boolean;
  visitationResponsibleContact?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  applicationStats?: {
    total: number;
    interested: number;
    notInterested: number;
    awaitingVisit: number;
    awaitingInterested: number;
    notReacted: number;
    candidates: {
      interested: Array<{
        id: number;
        name: string;
        email: string;
        phone: string;
        status: string;
        visitDate?: string | Date | null;
      }>;
      notInterested: Array<{
        id: number;
        name: string;
        email: string;
        phone: string;
        status: string;
        visitDate?: string | Date | null;
      }>;
      awaitingVisit: Array<{
        id: number;
        name: string;
        email: string;
        phone: string;
        status: string;
        visitDate?: string | Date | null;
      }>;
      awaitingInterested: Array<{
        id: number;
        name: string;
        email: string;
        phone: string;
        status: string;
        visitDate?: string | Date | null;
      }>;
      notReacted: Array<{
        id: number;
        name: string;
        email: string;
        phone: string;
        status: string;
        visitDate?: string | Date | null;
      }>;
    };
  };
  availableTimeslots?: number;
  timeslotDetails?: Array<{
    id: number;
    startTime: string | Date;
    endTime: string | Date;
    bookingCount: number;
    maxCapacity: number;
    bookings: Array<{
      type: string;
      name: string;
      email: string;
      phone: string;
      visitTime: string | Date;
    }>;
  }>;
  calculationDetails?: {
    ervPerMonth: number;
    daysVacant: number;
    monthsVacant: number;
    formula: string;
  };
  contracts?: Array<{
    id: number;
    phase: string;
    legalForm?: string;
    contractMilestoneDates?: Array<{
      id: number;
      milestoneDateType: string;
      date: string;
    }>;
    contractContacts?: Array<{
      id: number;
      contactType: string;
      resourceId?: number;
      resource?: { name?: string };
    }>;
  }>;
}

interface VacancyMetricsBlockNewProps {
  keyFigures?: {
    numUnitsInRent?: number;
    unitsInRentErv?: number;
    numUnitsInRenovation?: number;
  };
  vacancyDetails?: {
    units: VacancyUnit[];
    totalLostRevenue: number;
  };
  isLoading?: boolean;
  onRefresh?: () => Promise<void>;
}

interface TabTitleProps {
  title: string;
  count: number;
  color: string;
}

const TabTitle = ({ title, count, color }: TabTitleProps) => (
  <div className="flex items-center gap-2">
    <div className={`w-2 h-2 rounded-full ${color}`}></div>
    <span>{title}</span>
    <span className="text-neutral-secondary-mid">({count})</span>
  </div>
);

export const VacancyMetricsBlockNew = ({
  keyFigures,
  vacancyDetails,
  isLoading = false,
  onRefresh,
}: VacancyMetricsBlockNewProps) => {
  const safeKeyFigures = keyFigures ?? { numUnitsInRent: 0, unitsInRentErv: 0, numUnitsInRenovation: 0 };
  const safeVacancyDetails = vacancyDetails ?? { units: [], totalLostRevenue: 0 };
  const { numUnitsInRent, unitsInRentErv, numUnitsInRenovation } = safeKeyFigures;

  const navigate = useNavigate();
  const { session } = useSession();
  const [selectedTab, setSelectedTab] = useState("current");
  const [expandedUnitId, setExpandedUnitId] = useState<string | null>(null);
  const [editingMilestoneId, setEditingMilestoneId] = useState<number | null>(null);
  const [editingDate, setEditingDate] = useState<string>("");
  const [editingUnitBuiltAtId, setEditingUnitBuiltAtId] = useState<string | null>(null);
  const [editingUnitBuiltAt, setEditingUnitBuiltAt] = useState<string>("");

  const handleDeleteMilestone = async (milestoneId: number) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/milestone-dates/${milestoneId}`,
        { method: "DELETE" },
        session,
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      toast.success("Mijlpaal verwijderd");
      onRefresh?.();
    } catch (error) {
      console.error("Error deleting milestone:", error);
      toast.error("Fout bij verwijderen van mijlpaal");
    }
  };

  const handleSaveMilestone = async (milestoneId: number) => {
    if (!editingDate) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/milestone-dates/${milestoneId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ date: editingDate }),
        },
        session,
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      toast.success("Mijlpaal bijgewerkt");
      setEditingMilestoneId(null);
      setEditingDate("");
      onRefresh?.();
    } catch (error) {
      console.error("Error updating milestone:", error);
      toast.error("Fout bij bijwerken van mijlpaal");
    }
  };

  const handleSaveUnitBuiltAt = async (unitId: string) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/update/${unitId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ unitBuiltAt: editingUnitBuiltAt || null }),
        },
        session,
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      toast.success("Opleverdatum bijgewerkt");
      setEditingUnitBuiltAtId(null);
      setEditingUnitBuiltAt("");
      onRefresh?.();
    } catch (error) {
      console.error("Error updating unitBuiltAt:", error);
      toast.error("Fout bij bijwerken van opleverdatum");
    }
  };

  const currentVacantUnits = (safeVacancyDetails.units ?? []).filter(unit => unit.status === 'current');
  const upcomingVacantUnits = (safeVacancyDetails.units ?? []).filter(unit => unit.status === 'upcoming');

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('nl-BE');
  };

  const renderUnitRow = (unit: VacancyUnit) => (
    <div key={unit.id} className="border-b border-neutral-primary/5 last:border-b-0">
    <div
      onClick={() => navigate(`/building/${unit.building}#units`)}
      className="flex items-center justify-between px-3 py-2.5 hover:bg-moss-primary/5 transition-colors cursor-pointer"
    >
      {/* Left side - Unit info */}
      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
          unit.urgencyLevel === 'high' ? 'bg-red-500' : 
          unit.urgencyLevel === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
        }`}></div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5 mb-1 flex-wrap">
            <span className="font-bold text-sm text-foreground">{unit.building}</span>
            <span className="text-muted-foreground">•</span>
            <span className="font-semibold text-sm text-muted-foreground">Unit {unit.unitNumber}</span>
            <span className="text-muted-foreground">•</span>
            <span className="text-xs font-semibold text-foreground">{unit.daysVacant} dagen leeg</span>
          </div>
          
              {unit.vacancyReason === 'since_acquisition' ? (
            <div className="text-xs text-muted-foreground mb-1.5">
              Leeg sinds aankoop
            </div>
          ) : unit.lastTenantNames ? (
            <div className="text-xs text-muted-foreground mb-1.5">
              {unit.lastTenantNames}
            </div>
          ) : null}
          
          <div className="flex items-center gap-2 flex-wrap">
              {/* Immoweb Reference Status */}
              <Tooltip
                content={unit.immowebReferenceId ? 
                  `Open op Immoweb - Referentie #${unit.immowebReferenceId}` :
                  "Een immoweb referentie code invullen is cruciaal om kandidaten die via immoweb contact opnemen te capteren in de yallyhub als kandidaat"
                }
                placement="top"
              >
                <div className="flex items-center gap-1">
                  {unit.immowebReferenceId ? (
                    <a 
                      href={`https://www.immoweb.be/nl/zoekertje/${unit.immowebReferenceId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-moss-primary hover:text-moss-primary/80 transition-colors flex items-center gap-1"
                    >
                      <LinkSimpleIcon size={12} />
                      <span className="text-xs">Immoweb #{unit.immowebReferenceId}</span>
                    </a>
                  ) : (
                    <div className="flex items-center gap-1 text-gray-400 cursor-help">
                      <LinkSimpleIcon size={12} />
                      <XIcon size={12} className="text-red-500" />
                    </div>
                  )}
                </div>
              </Tooltip>
              
              {/* Calendar Publication Status */}
              <Tooltip
                content={unit.visibleInCalendar ? 'Gepubliceerd in afsprakenkalender' : 'Niet gepubliceerd in afsprakenkalender'}
                placement="top"
              >
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted border border-muted-foreground/50 transition-colors cursor-help">
                  <CalendarIcon size={12} className="text-muted-foreground" />
                  {unit.visibleInCalendar ? (
                    <CheckIcon size={12} className="text-status-good" />
                  ) : (
                    <XIcon size={12} className="text-red-500" />
                  )}
                </div>
              </Tooltip>

              {/* Visitation Responsible Contact */}
              <Tooltip
                content={unit.visitationResponsibleContact ? 
                  `Bezichtigingsverantwoordelijke: ${unit.visitationResponsibleContact.firstName} ${unit.visitationResponsibleContact.lastName}` :
                  "Geen bezichtigingsverantwoordelijke toegewezen"
                }
                placement="top"
              >
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted border border-muted-foreground/50 transition-colors cursor-help">
                  <UserIcon size={12} className="text-muted-foreground" />
                  {unit.visitationResponsibleContact ? (
                    <CheckIcon size={12} className="text-status-good" />
                  ) : (
                    <XIcon size={12} className="text-red-500" />
                  )}
                </div>
              </Tooltip>

              {/* Available Timeslots */}
              {unit.availableTimeslots !== undefined && (
                <Tooltip
                  content={
                    <div className="max-w-md p-2">
                      <div className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-300">
                        📅 Beschikbare tijdsloten
                      </div>
                      {unit.timeslotDetails && unit.timeslotDetails.length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {unit.timeslotDetails.map((slot, index) => (
                            <div key={slot.id} className={`p-3 rounded-lg bg-gray-50 ${index !== unit.timeslotDetails!.length - 1 ? 'mb-2' : ''}`}>
                              {/* Timeslot header */}
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-sm font-semibold text-gray-900">
                                  {new Date(slot.startTime).toLocaleString('nl-BE', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </div>
                                <div className={`text-xs font-medium px-2 py-1 rounded ${
                                  slot.bookingCount === slot.maxCapacity 
                                    ? 'bg-red-100 text-red-700' 
                                    : slot.bookingCount > 0 
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-green-100 text-green-700'
                                }`}>
                                  {slot.bookingCount}/{slot.maxCapacity} geboekt
                                </div>
                              </div>
                              
                              {/* Time range */}
                              <div className="text-sm text-gray-700 mb-2">
                                ⏰ {new Date(slot.startTime).toLocaleTimeString('nl-BE', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })} - {new Date(slot.endTime).toLocaleTimeString('nl-BE', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>

                              {/* Bookings */}
                              {slot.bookings && slot.bookings.length > 0 ? (
                                <div className="mt-2 pt-2 border-t border-gray-300">
                                  <div className="text-xs font-semibold text-gray-600 mb-2">Geboekt door:</div>
                                  <div className="space-y-2">
                                    {slot.bookings.map((booking, bookingIndex) => (
                                      <div key={bookingIndex} className="bg-white p-2 rounded-sm border border-gray-200">
                                        <div className="font-medium text-sm text-gray-900">{booking.name}</div>
                                        <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                          <div>📧 {booking.email}</div>
                                          <div>📱 {booking.phone}</div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs text-green-600 font-medium mt-2 pt-2 border-t border-gray-300">
                                  ✓ Nog volledig beschikbaar
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 text-center py-4">
                          Geen tijdslots beschikbaar
                        </div>
                      )}
                    </div>
                  }
                  placement="top"
                >
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full bg-muted border border-muted-foreground/50 transition-colors cursor-help ${
                    unit.availableTimeslots > 0 
                      ? '' 
                      : 'text-red-500'
                  }`}>
                    <ClockIcon size={12} className="text-muted-foreground" />
                    <span className="text-xs font-semibold">
                      {unit.availableTimeslots || 0}
                    </span>
                  </div>
                </Tooltip>
              )}

              {/* Application Stats */}
              {unit.applicationStats && unit.applicationStats.total > 0 && (
                <Tooltip
                  content={
                    <div className="max-w-lg p-2">
                      <div className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-300">
                        👥 Kandidaten (afgelopen maand)
                      </div>
                      
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {unit.applicationStats.candidates.interested.length > 0 && (
                          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                            <div className="font-semibold text-sm text-green-700 mb-2 flex items-center gap-2">
                              <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                {unit.applicationStats.interested}
                              </span>
                              ✓ Geïnteresseerd
                            </div>
                            <div className="space-y-2">
                              {unit.applicationStats.candidates.interested.map(candidate => (
                                <div key={candidate.id} className="bg-white p-2 rounded-sm border border-green-200">
                                  <div className="font-medium text-sm text-gray-900">{candidate.name || 'Naamloos'}</div>
                                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                    <div>📧 {candidate.email}</div>
                                    <div>📱 {candidate.phone}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {unit.applicationStats.candidates.awaitingVisit.length > 0 && (
                          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <div className="font-semibold text-sm text-blue-700 mb-2 flex items-center gap-2">
                              <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                {unit.applicationStats.awaitingVisit}
                              </span>
                              📅 Afwachtend bezoek
                            </div>
                            <div className="space-y-2">
                              {unit.applicationStats.candidates.awaitingVisit.map(candidate => (
                                <div key={candidate.id} className="bg-white p-2 rounded-sm border border-blue-200">
                                  <div className="font-medium text-sm text-gray-900">{candidate.name || 'Naamloos'}</div>
                                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                    <div>📧 {candidate.email}</div>
                                    <div>📱 {candidate.phone}</div>
                                    {candidate.visitDate && (
                                      <div className="text-blue-600 font-medium mt-1">
                                        📅 {new Date(candidate.visitDate).toLocaleString('nl-BE')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {unit.applicationStats.candidates.awaitingInterested.length > 0 && (
                          <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                            <div className="font-semibold text-sm text-purple-700 mb-2 flex items-center gap-2">
                              <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                {unit.applicationStats.awaitingInterested}
                              </span>
                              ⏳ Bezocht, geen feedback
                            </div>
                            <div className="space-y-2">
                              {unit.applicationStats.candidates.awaitingInterested.map(candidate => (
                                <div key={candidate.id} className="bg-white p-2 rounded-sm border border-purple-200">
                                  <div className="font-medium text-sm text-gray-900">{candidate.name || 'Naamloos'}</div>
                                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                    <div>📧 {candidate.email}</div>
                                    <div>📱 {candidate.phone}</div>
                                    {candidate.visitDate && (
                                      <div className="text-purple-600 font-medium mt-1">
                                        ✓ Bezocht: {new Date(candidate.visitDate).toLocaleString('nl-BE')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {unit.applicationStats.candidates.notInterested.length > 0 && (
                          <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                            <div className="font-semibold text-sm text-red-700 mb-2 flex items-center gap-2">
                              <span className="bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                {unit.applicationStats.notInterested}
                              </span>
                              ✗ Niet geïnteresseerd
                            </div>
                            <div className="space-y-2">
                              {unit.applicationStats.candidates.notInterested.map(candidate => (
                                <div key={candidate.id} className="bg-white p-2 rounded-sm border border-red-200">
                                  <div className="font-medium text-sm text-gray-900">{candidate.name || 'Naamloos'}</div>
                                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                    <div>📧 {candidate.email}</div>
                                    <div>📱 {candidate.phone}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {unit.applicationStats.candidates.notReacted.length > 0 && (
                          <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                            <div className="font-semibold text-sm text-orange-700 mb-2 flex items-center gap-2">
                              <span className="bg-orange-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                {unit.applicationStats.notReacted}
                              </span>
                              ⚠️ Geen bezoek gepland
                            </div>
                            <div className="space-y-2">
                              {unit.applicationStats.candidates.notReacted.map(candidate => (
                                <div key={candidate.id} className="bg-white p-2 rounded-sm border border-orange-200">
                                  <div className="font-medium text-sm text-gray-900">{candidate.name || 'Naamloos'}</div>
                                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                    <div>📧 {candidate.email}</div>
                                    <div>📱 {candidate.phone}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  }
                  placement="top"
                >
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted border border-muted-foreground/50 transition-colors cursor-help">
                    <UsersThreeIcon size={12} className="text-muted-foreground" />
                    <span className="text-xs font-semibold">
                      {unit.applicationStats.total}
                    </span>
                  </div>
                </Tooltip>
              )}
          </div>
        </div>
      </div>

      {/* Right side - Financial Info + Expand button */}
      <div className="flex items-center gap-2 shrink-0 ml-3">
        <div className="text-right">
          {unit.status === 'current' && unit.daysVacant !== undefined && unit.daysVacant > 0 ? (
            <Tooltip
              content={
                <div className="max-w-sm p-2">
                  <div className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-300">
                    Gedetailleerde berekening
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      {unit.vacancyReason === 'since_acquisition' ? (
                        <div className="space-y-1">
                          <div className="font-semibold text-gray-900">Leeg sinds aankoop</div>
                          <div className="text-gray-600">
                            {unit.buildingAcquisitionDate ? new Date(unit.buildingAcquisitionDate).toLocaleDateString('nl-BE') : 'Onbekend'}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <div className="font-semibold text-gray-900">Contract einddatum</div>
                          <div className="text-gray-600">
                            {unit.contractEndDate ? new Date(unit.contractEndDate).toLocaleDateString('nl-BE') : 'Onbekend'}
                          </div>
                          {unit.lastTenantNames && (
                            <div className="mt-2 pt-2 border-t border-gray-300">
                              <div className="text-xs text-gray-500">Laatste huurder</div>
                              <div className="text-gray-900 font-medium">{unit.lastTenantNames}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-blue-50 p-2 rounded-sm">
                        <div className="text-xs text-blue-600">Dagen leeg</div>
                        <div className="font-bold text-blue-900">{unit.daysVacant}</div>
                      </div>
                      <div className="bg-blue-50 p-2 rounded-sm">
                        <div className="text-xs text-blue-600">Maanden</div>
                        <div className="font-bold text-blue-900">{unit.calculationDetails?.monthsVacant.toFixed(2)}</div>
                      </div>
                    </div>

                    <div className="bg-green-50 p-2 rounded-sm">
                      <div className="text-xs text-green-600">ERV per maand</div>
                      <div className="font-bold text-green-900">{unit.erv.toLocaleString('nl-BE')}</div>
                    </div>

                    <div className="bg-red-50 p-3 rounded-sm border-2 border-red-200">
                      <div className="text-xs text-red-600 mb-1">Totaal verlies</div>
                      <div className="font-mono text-xs text-gray-600 mb-2">
                        {unit.erv} x {unit.calculationDetails?.monthsVacant.toFixed(2)} maanden
                      </div>
                      <div className="font-bold text-lg text-red-700">
                        {unit.lostRevenue?.toLocaleString('nl-BE')}
                      </div>
                    </div>
                  </div>
                </div>
              }
              placement="top"
            >
              <div className="cursor-help">
                <div className="text-sm font-semibold text-red-600">
                  -{applyMask(unit.lostRevenue || 0, "valuta")}
                </div>
                <div className="text-xs text-neutral-secondary-mid">
                  {applyMask(unit.erv, "valuta")}/mnd
                </div>
              </div>
            </Tooltip>
          ) : (
            <div>
              <div className="text-sm font-semibold text-neutral-primary">
                {applyMask(unit.erv, "valuta")}/mnd
              </div>
              {unit.status === 'upcoming' && (
                <div className="text-xs text-neutral-secondary-mid">
                  Binnenkort leeg
                </div>
              )}
            </div>
          )}
        </div>

        {/* Expand contract details button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpandedUnitId(expandedUnitId === unit.id ? null : unit.id);
          }}
          className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          title="Contractgegevens bekijken"
        >
          {expandedUnitId === unit.id ? <CaretUpIcon size={14} /> : <CaretDownIcon size={14} />}
        </button>
      </div>
    </div>

    {/* Expandable contract details panel */}
    {expandedUnitId === unit.id && (
      <div className="px-3 pb-3 bg-muted/30" onClick={(e) => e.stopPropagation()}>
        <div className="rounded-lg border border-neutral-primary/10 bg-background p-3 space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            <FileTextIcon size={14} />
            Contractgegevens
          </div>

          {/* Opleverdatum — shows unitBuiltAt if set, otherwise falls back to building acquisition date */}
          <div className="text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <span>Opleverdatum (eerste introductie binnen portefeuille):</span>
              {editingUnitBuiltAtId === unit.id ? (
                <div className="inline-flex items-center gap-1">
                  <input
                    type="date"
                    value={editingUnitBuiltAt}
                    onChange={(e) => setEditingUnitBuiltAt(e.target.value)}
                    className="text-xs border border-neutral-primary/20 rounded px-1 py-0.5 bg-background text-foreground"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSaveUnitBuiltAt(unit.id); }}
                    className="p-0.5 rounded hover:bg-green-200 text-green-700 transition-colors"
                    title="Opslaan"
                  >
                    <FloppyDiskIcon size={12} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingUnitBuiltAtId(null); setEditingUnitBuiltAt(""); }}
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground transition-colors"
                    title="Annuleren"
                  >
                    <XIcon size={12} />
                  </button>
                </div>
              ) : (
                <div className="inline-flex items-center gap-1">
                  <span className="font-medium text-foreground">
                    {formatDate(unit.unitBuiltAt || unit.buildingAcquisitionDate)}
                  </span>
                  {!unit.unitBuiltAt && unit.buildingAcquisitionDate && (
                    <span className="text-[10px] text-muted-foreground italic">(aankoopdatum gebouw)</span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingUnitBuiltAtId(unit.id);
                      const fallbackDate = unit.unitBuiltAt || unit.buildingAcquisitionDate;
                      setEditingUnitBuiltAt(fallbackDate ? new Date(fallbackDate).toISOString().split('T')[0] : '');
                    }}
                    className="p-0.5 rounded hover:bg-blue-200 text-blue-600 transition-colors"
                    title="Opleverdatum bewerken"
                  >
                    <PencilSimpleIcon size={12} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {(!unit.contracts || unit.contracts.length === 0) ? (
            <div className="text-xs text-red-500 font-medium py-2">
              Geen contracten gevonden voor deze unit
            </div>
          ) : (
            <div className="space-y-2">
              {unit.contracts.map((contract) => (
                <div key={contract.id} className="rounded border border-neutral-primary/10 p-2.5 space-y-2">
                  {/* Contract header */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-foreground">
                      Contract #{contract.id}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {contract.phase}
                      </span>
                      {contract.legalForm && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {contract.legalForm}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Milestones */}
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Mijlpalen:</div>
                    {(!contract.contractMilestoneDates || contract.contractMilestoneDates.length === 0) ? (
                      <div className="text-xs text-red-500">Geen mijlpalen</div>
                    ) : (
                      <div className="space-y-1">
                        {contract.contractMilestoneDates.map((m) => {
                          const isEndDate = m.milestoneDateType === 'Einddatum';
                          const isEditing = editingMilestoneId === m.id;
                          return (
                            <div
                              key={m.id}
                              className={`text-xs px-1.5 py-1 rounded flex items-center justify-between gap-1 ${isEndDate ? 'bg-red-50 text-red-700 font-semibold border border-red-200' : 'bg-muted text-muted-foreground'}`}
                            >
                              <div className="flex items-center gap-1 flex-1 min-w-0">
                                <span className="shrink-0">{m.milestoneDateType}:</span>
                                {isEditing ? (
                                  <input
                                    type="date"
                                    value={editingDate}
                                    onChange={(e) => setEditingDate(e.target.value)}
                                    className="text-xs border border-neutral-primary/20 rounded px-1 py-0.5 bg-background text-foreground"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                ) : (
                                  <span>{formatDate(m.date)}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-0.5 shrink-0">
                                {isEditing ? (
                                  <>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleSaveMilestone(m.id); }}
                                      className="p-0.5 rounded hover:bg-green-200 text-green-700 transition-colors"
                                      title="Opslaan"
                                    >
                                      <FloppyDiskIcon size={12} />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setEditingMilestoneId(null); setEditingDate(""); }}
                                      className="p-0.5 rounded hover:bg-muted text-muted-foreground transition-colors"
                                      title="Annuleren"
                                    >
                                      <XIcon size={12} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingMilestoneId(m.id);
                                        const d = new Date(m.date);
                                        setEditingDate(d.toISOString().split('T')[0]);
                                      }}
                                      className="p-0.5 rounded hover:bg-blue-200 text-blue-600 transition-colors"
                                      title="Datum bewerken"
                                    >
                                      <PencilSimpleIcon size={12} />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDeleteMilestone(m.id); }}
                                      className="p-0.5 rounded hover:bg-red-200 text-red-600 transition-colors"
                                      title="Verwijderen"
                                    >
                                      <TrashIcon size={12} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Contacts */}
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Contacten:</div>
                    {(!contract.contractContacts || contract.contractContacts.length === 0) ? (
                      <div className="text-xs text-red-500">Geen contacten</div>
                    ) : (
                      <div className="space-y-1">
                        {contract.contractContacts.map((c) => {
                          const isTenant = c.contactType === 'Contractant (huurder)';
                          return (
                            <div
                              key={c.id}
                              className={`text-xs px-1.5 py-1 rounded flex items-center justify-between ${isTenant ? 'bg-blue-50 text-blue-700 font-semibold border border-blue-200' : 'bg-muted text-muted-foreground'}`}
                            >
                              <span>{c.resource?.name || `Resource #${c.resourceId || '?'}`}</span>
                              <span className="text-[10px] opacity-75">{c.contactType}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Vacancy calculation summary */}
          <div className="rounded border border-neutral-primary/10 p-2.5 bg-muted/50">
            <div className="text-xs font-semibold text-muted-foreground mb-1">Berekening leegstand:</div>
            <div className="text-xs space-y-0.5">
              <div>Leeg sinds: <span className="font-medium text-foreground">{unit.vacantSince ? formatDate(unit.vacantSince) : 'Niet bepaald'}</span></div>
              <div>Reden: <span className="font-medium text-foreground">
                {unit.vacancyReason === 'since_acquisition'
                  ? (unit.unitBuiltAt ? 'Sinds oplevering unit' : 'Sinds aankoop gebouw')
                  : unit.vacancyReason === 'contract_end' ? 'Einde contract' : unit.vacancyReason || 'Onbekend'}
              </span></div>
              <div>Dagen leeg: <span className="font-medium text-foreground">{unit.daysVacant ?? 0}</span></div>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<RepeatIcon weight="duotone" size={20} />}
        title="Units in herverhuur"
      />
      <DashboardBlockContent padding="none">
        {isLoading ? (
          <BlockLoadingState message="Verhuur gegevens aan het ophalen..." />
        ) : (
          <>
            {(safeVacancyDetails.units ?? []).length > 0 ? (
              <>
                {/* Tabs Section */}
                <div className="px-4 pt-4">
                  <Tabs
                    selectedKey={selectedTab}
                    onSelectionChange={(key) => setSelectedTab(key as string)}
                    variant="underlined"
                    fullWidth
                    classNames={{
                      base: "shadow-[inset_0_-1px_0_0_rgba(49,64,59,0.2)]",
                      tabList: "p-0 gap-0",
                      tab: "text-base px-0 py-0 h-fit flex-1",
                      tabContent: "py-2 w-full text-neutral-secondary-mid group-data-[selected=true]:text-brick-primary",
                      cursor: "w-full bg-brick-primary",
                    }}
                  >
                    <Tab
                      key="current"
                      title={<TabTitle title="Momenteel leegstaand" count={currentVacantUnits.length} color="bg-red-500" />}
                    >
                      <div className="py-4">
                        {currentVacantUnits.length > 0 ? (
                          <div className="flex flex-col">
                            {currentVacantUnits.map(renderUnitRow)}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            Geen leegstaande units
                          </div>
                        )}
                      </div>
                    </Tab>
                    <Tab
                      key="upcoming"
                      title={<TabTitle title="Komt binnenkort leeg" count={upcomingVacantUnits.length} color="bg-yellow-500" />}
                    >
                      <div className="py-4">
                        {upcomingVacantUnits.length > 0 ? (
                          <div className="flex flex-col">
                            {upcomingVacantUnits.map(renderUnitRow)}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            Geen units die binnenkort leeg komen
                          </div>
                        )}
                      </div>
                    </Tab>
                  </Tabs>
                </div>

                {/* Stats Section - Moved to Bottom */}
                <div className="border-t border-border">
                  {/* Total Portfolio Stats */}
                  <div className="px-4 pt-4 pb-3 border-b border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Portfolio Verhuurd</span>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg font-bold text-foreground">
                          {(typeof numUnitsInRent === 'number' ? numUnitsInRent : null) ?? (
                            <Spinner size="sm" color="warning" />
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <span>units •</span>
                          {typeof unitsInRentErv === 'number' ? (
                            <span>{applyMask(unitsInRentErv, "valuta")}/mnd</span>
                          ) : (
                            <Spinner size="sm" color="warning" />
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Vacancy Overview Stats */}
                  <div className="px-4 py-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="flex flex-col">
                        <div className="text-2xl font-bold text-neutral-primary mb-1">
                          {currentVacantUnits.length}
                        </div>
                        <div className="text-xs text-neutral-secondary-mid">Momenteel leeg</div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-2xl font-bold text-neutral-primary mb-1">
                          {upcomingVacantUnits.length}
                        </div>
                        <div className="text-xs text-neutral-secondary-mid">Binnenkort leeg</div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-2xl font-bold text-neutral-primary mb-1">
                          {(safeVacancyDetails.units ?? []).reduce((sum, unit) => sum + (unit.daysVacant || 0), 0)}
                        </div>
                        <div className="text-xs text-neutral-secondary-mid">Dagen leegstand</div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-2xl font-bold text-red-600 mb-1">
                          {safeVacancyDetails.totalLostRevenue ? applyMask(safeVacancyDetails.totalLostRevenue, "valuta") : '€0'}
                        </div>
                        <div className="text-xs text-neutral-secondary-mid">Gederfde inkomsten</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-4">
                <BlockEmptyState
                  icon={<div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-green-500"></div>
                  </div>}
                  title="Alle units zijn verhuurd"
                  description={`excl. ${numUnitsInRenovation ?? 0} leegstaande units in renovatie`}
                />
              </div>
            )}
          </>
        )}
      </DashboardBlockContent>
    </DashboardBlock>
  );
};

